/**
 * PatientAddressList – Table of patient addresses (訪問住所一覧)
 * Columns: 連番, 住所, 名称, 常時, 操作.
 * Table and 操作 buttons follow design-template "6. Data Table (データテーブル)".
 */

import React, { useMemo } from 'react';
import { AppTable, AppSpin, AppTableActionButton, AppTooltip } from '@/components/atoms';
import type { PatientAddress } from '../types';
import type { ColumnsType } from 'antd/es/table';

function getAddressText(record: PatientAddress): string {
  const parts = [record.address1, record.address2, record.address3].filter(Boolean);
  return parts.join('') || '';
}

const EMPTY_LABEL = '';

interface PatientAddressListProps {
  list: PatientAddress[];
  onEdit: (address: PatientAddress) => void;
  onDelete: (address: PatientAddress) => void;
  isLoading?: boolean;
}

const PatientAddressList: React.FC<PatientAddressListProps> = ({
  list,
  onEdit,
  onDelete,
  isLoading,
}) => {
  const columns: ColumnsType<PatientAddress> = useMemo(
    () => [
      {
        title: '連番',
        key: 'serial_number',
        width: 44,
        align: 'center',
        sorter: (a, b) => (a.serial_number ?? 0) - (b.serial_number ?? 0),
        render: (_: unknown, record: PatientAddress) =>
          record.serial_number != null ? String(record.serial_number) : EMPTY_LABEL,
      },
      {
        title: '住所',
        key: 'address',
        align: 'left',
        className: 'min-w-0',
        sorter: (a, b) => getAddressText(a).localeCompare(getAddressText(b)),
        render: (_: unknown, record: PatientAddress) => {
          const text = getAddressText(record) || EMPTY_LABEL;
          return (
            <AppTooltip title={text} showOnlyOnOverflow>
              <div className="break-words line-clamp-2">{text}</div>
            </AppTooltip>
          );
        },
      },
      {
        title: '名称',
        key: 'name',
        width: 300,
        align: 'left',
        className: 'min-w-0',
        sorter: (a, b) => (a.name ?? EMPTY_LABEL).localeCompare(b.name ?? EMPTY_LABEL),
        render: (_: unknown, record: PatientAddress) => {
          const text = record.name?.trim() || EMPTY_LABEL;
          return (
            <AppTooltip title={text} showOnlyOnOverflow>
              <div className="break-words line-clamp-2">{text}</div>
            </AppTooltip>
          );
        },
      },
      {
        title: '常時',
        key: 'is_default',
        width: 150,
        align: 'center',
        sorter: (a, b) => (a.is_default ? 1 : 0) - (b.is_default ? 1 : 0),
        render: (_: unknown, record: PatientAddress) => (record.is_default === true ? '〇' : ''),
      },
      {
        title: '操作',
        key: 'action',
        width: 170,
        align: 'center' as const,
        render: (_: unknown, record: PatientAddress) => (
          <div className="flex justify-center gap-1">
            <AppTableActionButton action="edit" onClick={() => onEdit(record)} title="編集">
              編集
            </AppTableActionButton>
            <AppTableActionButton action="delete" onClick={() => onDelete(record)} title="削除">
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    [onEdit, onDelete]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <AppSpin />
      </div>
    );
  }

  return (
    <div className="app-table-last-col-fit">
      <AppTable<PatientAddress>
        columns={columns}
        dataSource={list}
        rowKey="id"
        pagination={false}
        tableLayout="auto"
        loading={false}
        recordCount={list.length}
        customLabelTotal="検索結果"
      />
    </div>
  );
};

export default PatientAddressList;
